import crypto from "crypto";
import prisma from "@/lib/prisma";

/**
 * Owner access tokens are random 32-byte values encoded as base64url. They're
 * included in the pet-complete email ("Manage {name}'s memorial") and let the
 * owner edit bio/age and generate a tribute without signing in.
 *
 * Tokens are single-use in the sense that one token = one memorial; they don't
 * expire. If the owner leaks the URL, anyone with it can edit the memorial —
 * but that's the same risk profile as the memorial URL itself (which is also
 * public). Revocation would mean re-issuing a new token, which we don't wire
 * up yet.
 */

export function generateOwnerAccessToken(): string {
  return crypto.randomBytes(32).toString("base64url");
}

/**
 * Ensures the pet has an ownerAccessToken, generating one if missing. Returns
 * the final token. Safe to call repeatedly (no-op if one already exists).
 */
export async function ensureOwnerAccessToken(petId: string): Promise<string> {
  const pet = await prisma.pet.findUnique({
    where: { id: petId },
    select: { ownerAccessToken: true },
  });
  if (pet?.ownerAccessToken) return pet.ownerAccessToken;

  const token = generateOwnerAccessToken();
  await prisma.pet.update({
    where: { id: petId },
    data: { ownerAccessToken: token },
  });
  return token;
}

/**
 * Constant-time comparison of a provided token against a pet's stored token.
 * Returns the pet if valid, null otherwise. Never throws on mismatch.
 */
export async function verifyOwnerAccessToken(
  petId: string,
  providedToken: string | null | undefined
): Promise<{ id: string } | null> {
  if (!providedToken || typeof providedToken !== "string") return null;

  const pet = await prisma.pet.findUnique({
    where: { id: petId },
    select: { id: true, ownerAccessToken: true },
  });
  if (!pet?.ownerAccessToken) return null;

  const a = Buffer.from(pet.ownerAccessToken);
  const b = Buffer.from(providedToken);
  if (a.length !== b.length) return null;
  if (!crypto.timingSafeEqual(a, b)) return null;

  return { id: pet.id };
}

/**
 * Build the owner-mode URL for a pet's memorial page. This is the live
 * memorial page with the ?token= query param — destructive UI and inline
 * editing unlock when the token validates server-side. Used everywhere we
 * hand an owner a link (delivery email, admin "copy owner link" button).
 */
export function buildOwnerMemorialUrl(petId: string, token: string, baseUrl?: string): string {
  const base =
    baseUrl ||
    process.env.NEXT_PUBLIC_NEXTAUTH_URL ||
    process.env.NEXT_PUBLIC_APP_URL ||
    "https://www.petaftercare.co.nz";
  return `${base}/memorial/${petId}?token=${encodeURIComponent(token)}`;
}

/**
 * Appends `?token=<token>` (or `&token=<token>`) to an existing href, used by
 * intra-app navigation so the owner-mode unlock survives a hop through the
 * keepsake catalogue. No-op when `token` is null/empty — for non-owner
 * visitors the link works identically. The href argument is treated as
 * opaque; existing query strings are preserved.
 */
export function appendOwnerToken(
  href: string,
  token: string | null | undefined
): string {
  if (!token) return href;
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}token=${encodeURIComponent(token)}`;
}
